/**
 * Pages reference kind (P-1) — heals the TransformPath references Pages
 * serializes, which the registry previously did not track (they silently
 * stranded on rename/reparent):
 *
 *   - `pages[pageIndex].states[assignmentIndex].target`
 *   - `shared[sharedIndex]` (shared-children paths)
 *
 * Targets are plain VALUES, so healing is a simple field rewrite (no key
 * surgery — the pre-restructure schema kept paths as map keys, which needed
 * delete+reinsert and a NUL-separated location key; the array schema removed
 * that).
 *
 * Location keys (kind-internal, opaque to the registry) are positional:
 *   - page state assignments: `ps {pageIndex} {assignmentIndex}`
 *   - shared-children paths:  `sh {sharedIndex}`
 *
 * Page ordinals themselves (array positions) are a separate durability
 * concern (P-2), handled at the reorder-commit site — this kind only heals
 * the path STRINGS.
 */

import type { SceneObject } from '../../../core/SceneObject';
import type { PagesData } from '../../../scenedata/behaviors/pagesdata';
import type { ReferenceKind, ReferenceLocation } from '../referenceIdentity';

interface PagesLike {
    getData?: () => PagesData | undefined;
    commit?: (patch: Partial<PagesData>) => Promise<unknown>;
}

function pagesOf(owner: SceneObject): PagesLike | null {
    return (owner.getBehavior?.('Pages') as PagesLike | null | undefined) ?? null;
}

function dataOf(p: PagesLike | null): PagesData | undefined {
    try {
        return p?.getData?.();
    } catch {
        return undefined;
    }
}

export const pagesReferenceKind: ReferenceKind = {
    behaviorType: 'Pages',

    discover(owner: SceneObject): ReferenceLocation[] {
        const data = dataOf(pagesOf(owner));
        if (!data) return [];
        const out: ReferenceLocation[] = [];

        (data.pages ?? []).forEach((page, pi) => {
            (page?.states ?? []).forEach((a, ai) => {
                out.push({ key: `ps ${pi} ${ai}`, path: a.target });
            });
        });
        (data.shared ?? []).forEach((path, si) => {
            out.push({ key: `sh ${si}`, path });
        });
        return out;
    },

    async write(owner: SceneObject, updates: Array<{ key: string; path: string }>): Promise<void> {
        const p = pagesOf(owner);
        const data = dataOf(p);
        if (!data || !p?.commit) return;

        // Deep-ish clone of the structures we mutate.
        const pages = (data.pages ?? []).map(page => ({
            ...page,
            states: page?.states?.map(a => ({ ...a })),
        }));
        const shared = [...(data.shared ?? [])];

        for (const { key, path: newPath } of updates) {
            const [field, a, b] = key.split(' ');
            if (field === 'ps') {
                const assignment = pages[Number(a)]?.states?.[Number(b)];
                if (assignment) assignment.target = newPath;
            } else if (field === 'sh') {
                if (Number(a) < shared.length) shared[Number(a)] = newPath;
            }
        }

        await p.commit({
            pages: pages.length > 0 ? pages : undefined,
            shared: shared.length > 0 ? shared : undefined,
        });
    },
};
